import { Badge } from '@/modules/Badge';
import { BadgeColor } from '@/shared/types/badge';
import { cn } from '@/shared/utils/cn';
import React from 'react';

export interface ListItemUserProps {
  name: string;
  subtitle?: string;
  avatar?: React.ReactNode | string;
  badge?: string;
  badgeVariant?: 'success' | 'warning' | 'error' | 'info';
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

const badgeStyles = {
  success: 'ui-bg-surface-success ui-text-success ui-border-green-200',
  warning: 'ui-bg-surface-warning ui-text-warning ui-border-yellow-200',
  error: 'ui-bg-surface-danger ui-text-danger ui-border-red-200',
  info: 'ui-bg-surface-info ui-text-info ui-border-blue-200',
};

export const ListItemUser: React.FC<ListItemUserProps> = ({
  name,
  subtitle,
  avatar,
  badge,
  badgeVariant = 'success',
  selected = false,
  onClick,
  className,
}) => {
  const renderAvatar = () => {
    if (typeof avatar === 'string') {
      return (
        <div className="ui-flex ui-h-32 ui-w-32 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-surface-secondary ui-text-body-md-regular ui-font-body-md-medium ui-text-secondary">
          {avatar}
        </div>
      );
    }
    // Cuando avatar es ReactNode (ej. imagen o componente), envolver con pointer-events:none
    // para que el clic llegue al ítem y la selección funcione siempre
    if (avatar !== null && avatar !== undefined) {
      return <span style={{ pointerEvents: 'none' }}>{avatar}</span>;
    }
    return null;
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'ui-flex ui-h-56 ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6 ui-transition-all',
        {
          'ui-cursor-pointer': onClick,
          // Hover: gris claro
          'hover:ui-bg-fill-hover': onClick && !selected,
          // Selected: azul oscuro con texto blanco
          'ui-bg-fill-brand': selected,
        },
        className
      )}
    >
      {/* Avatar */}
      {renderAvatar()}

      {/* Content */}
      <div className="ui-min-w-0 ui-flex-1">
        <div
          className={cn(
            'ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-leading-body-sm-regular ui-tracking-body-sm',
            {
              'ui-text-on-color': selected,
              'ui-text-primary': !selected,
            }
          )}
        >
          {name}
        </div>
        {subtitle && (
          <div
            className={cn(
              'ui-truncate ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
              {
                'ui-text-on-color ui-opacity-70': selected,
                'ui-text-secondary': !selected,
              }
            )}
          >
            {subtitle}
          </div>
        )}
      </div>

      {/* Badge */}
      {badge && (
        <Badge label={badge} color={badgeVariant as BadgeColor} theme="soft" stroke size="md" />
      )}
    </div>
  );
};
